import { useEffect, useState } from 'react';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { useLoader } from '../../../hooks/useLoader';
import { Table, Pagination, Row, Cell, MenuItem, Button, Input } from '@dsi/react-eds';
import { PBNodesDeploy } from './moshell-endpoint-interfaces';
import styles from './MoshellNodesList.module.css';

interface PropsObjects {
  moshellId: string;
  nodes: PBNodesDeploy[];
  savedNodes: PBNodesDeploy[];
  selectNodes: (nodes: PBNodesDeploy[]) => void;
}

const MoshellNodesList = (props: PropsObjects) => {
  const [data, setData] = useState<PBNodesDeploy[]>(props.nodes);
  const { showLoader, hideLoader } = useLoader();
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filteredTableData, setFilteredTableData] = useState<PBNodesDeploy[]>(props.nodes);
  const rowsPerPage = 5;
  const selectedCount = data.reduce((acc, data) => {
    if (data.selected) {
      return acc + 1;
    }
    return acc;
  }, 0);

  useEffect(() => {
    const validateEnmIsLinked = (nodesSaved: PBNodesDeploy[], nodesData: PBNodesDeploy[]) => {
      if (nodesSaved.length > 0) {
        nodesSaved.forEach((el) => {
          const found = nodesData.find((ed) => ed.name === el.name);
          if (found) {
            found.selected = true;
          }
        });
      }
      return nodesData;
    };
    if (props.savedNodes.length > 0) {
      const nodes = validateEnmIsLinked(props.savedNodes, props.nodes);
      setData(nodes);
      setFilteredTableData(nodes);
    } else {
      setData(props.nodes);
      setFilteredTableData(props.nodes);
    }
  }, [props.moshellId, props.nodes]);

  const columns = [
    { title: 'Name', attribute: 'name' },
    { title: 'IP', attribute: 'ip' }
  ];

  const handleSelectDeselectAll = (type: boolean) => {
    showLoader();
    const selectedData = data.map((data) => {
      return { ...data, selected: type };
    });
    setData(selectedData);
    props.selectNodes(selectedData.filter((sd) => sd.selected));
    hideLoader();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectedRow = (e: any) => {
    const newData = data;
    const newFilteredData = filteredTableData.map((td: PBNodesDeploy, i: number) => {
      const found = newData.find((d) => d.name === td.name && d.ip === td.ip);
      const wasSelected = e.includes(i);
      td.selected = wasSelected;
      if (found) {
        found.selected = wasSelected;
      }
      return td;
    });
    setData(newData);
    setFilteredTableData(newFilteredData);
    props.selectNodes(newData.filter((sd) => sd.selected));
  };

  return (
    <div>
      <div className="table-top">
        <div className="table-top-left">
          <div className="table-info">
            <span>
              {(page - 1) * rowsPerPage + 1} - {Math.min(page * rowsPerPage, data.length)} of
            </span>
            <span> {data.length} items</span>
            <span>{selectedCount > 0 && ` | ${selectedCount} selected`}</span>
          </div>
        </div>
        <div className="table-top-right">
          <div className="table-buttons">
            <ReactEDSWrapper component={Button} icon={showFilters ? 'filter-off' : 'filter'} onClick={() => setShowFilters(!showFilters)}>
              Filter
            </ReactEDSWrapper>
          </div>
        </div>
      </div>

      <ReactEDSWrapper
        component={Table}
        id="moshell-nodes-table"
        columns={columns}
        multiSelect={filteredTableData?.length > 0}
        checkboxDropdownItems={[
          <ReactEDSWrapper
            component={MenuItem}
            key="select-all"
            value="select-all"
            disabled={selectedCount === data.length}
            onClick={() => handleSelectDeselectAll(true)}
          >
            Select items from all pages
          </ReactEDSWrapper>,
          <ReactEDSWrapper
            component={MenuItem}
            key="deselect-all"
            value="deselect-all"
            disabled={selectedCount === 0}
            onClick={() => handleSelectDeselectAll(false)}
          >
            Deselect items from all pages
          </ReactEDSWrapper>
        ]}
        filters={
          <ReactEDSWrapper component={Row}>
            {columns.map((column) => (
              <ReactEDSWrapper key={column.title} component={Cell}>
                <ReactEDSWrapper
                  key={`${column.title}_filter`}
                  component={Input}
                  id={`${column.title}_input`}
                  placeholder="Filter"
                  fullwidth
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFilteredTableData(
                      data.filter((record) => record[column.attribute].toLowerCase().includes(e.target.value.toLowerCase()))
                    )
                  }
                ></ReactEDSWrapper>
              </ReactEDSWrapper>
            ))}
          </ReactEDSWrapper>
        }
        showFilters={showFilters}
        onSelect={(e) => handleSelectedRow(e)}
      >
        {filteredTableData?.length === 0 ? (
          <ReactEDSWrapper component={Row} key={'no-data'} selected={false}>
            <ReactEDSWrapper component={Cell} colSpan={columns.length} key={'no-data'}>
              <div className={styles.noDataContainer}>
                <i className={`icon icon-info ${styles.dangerIcon}`} />
                <div className={styles.noDataTitle}>No Nodes Found</div>
              </div>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
        ) : (
          filteredTableData.map((row) => (
            <ReactEDSWrapper component={Row} key={row.name} selected={row.selected}>
              {columns.map(({ attribute }) => (
                <ReactEDSWrapper component={Cell} key={attribute}>
                  {row[attribute]}
                </ReactEDSWrapper>
              ))}
            </ReactEDSWrapper>
          ))
        )}
      </ReactEDSWrapper>
      <ReactEDSWrapper
        component={Pagination}
        numPages={Math.ceil(data.length / rowsPerPage)}
        selectedPage={page}
        onPaginationChangePage={setPage}
      />
    </div>
  );
};
export default MoshellNodesList;
